import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { mockRoles, type TenantRole } from '@/mocks/roles';

function buildFallbackUserRole(
  role: string,
  permissions: string[],
): TenantRole {
  return {
    _id: `role-${role}`,
    name: role,
    alias: role,
    permissionKeys: permissions,
    isSystem: true,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Built-in system role → permission mapping.
 * Dùng làm fallback khi BE chưa trả `permissions` trong auth response.
 */
function getSystemRolePermissions(role: string): string[] {
  const sysRole = mockRoles.find(
    (r) => r.isSystem && (r.alias === role || r._id === `role-sys-${role}`),
  );
  return sysRole?.permissionKeys ?? [];
}

export function useRbac() {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    const fromBe = user?.permissions;
    // Nếu BE trả permissions (kể cả mảng rỗng), dùng luôn
    if (fromBe && Array.isArray(fromBe)) {
      // BE trả mảng không rỗng → dùng BE data
      if (fromBe.length > 0) return fromBe;
      // BE trả mảng rỗng → fallback tính từ role definitions
      if (user?.role) return getSystemRolePermissions(user.role);
      return [];
    }
    // BE chưa trả permissions field → fallback tính từ role definitions
    if (user?.role) return getSystemRolePermissions(user.role);
    return [];
  }, [user?.permissions, user?.role]);

  const userRoles = useMemo(() => {
    if (!user) return [];
    return [buildFallbackUserRole(user.role, permissions)];
  }, [user, permissions]);

  const hasPermission = useMemo(() => {
    const isSuper = permissions.includes('*');
    return (key: string) => isSuper || permissions.includes(key);
  }, [permissions]);

  const hasAny = useMemo(() => {
    const isSuper = permissions.includes('*');
    return (keys: string[]) => isSuper || keys.some((k) => permissions.includes(k));
  }, [permissions]);

  return { permissions, userRoles, hasPermission, hasAny, isLoading: false };
}
